/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { RotateCcw, Home, Skull, Award, PlayCircle } from 'lucide-react';

interface GameOverOverlayProps {
  score: number;
  bestScore: number;
  levelReached: number;
  kills: number;
  difficulty: 'rookie' | 'veteran' | 'ace';
  onRestart: () => void;
  onExit: () => void;
}

export const GameOverOverlay: React.FC<GameOverOverlayProps> = ({
  score,
  bestScore,
  levelReached,
  kills,
  difficulty,
  onRestart,
  onExit,
}) => {
  const isNewRecord = score >= bestScore && score > 0;

  return (
    <div id="gameover-overlay" className="absolute inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-6 z-25">
      <div className="w-full max-w-md bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-800 rounded-2xl p-6 shadow-2xl text-slate-200">
        
        {/* Header alert */}
        <div className="text-center space-y-2">
          <div className="w-12 h-12 bg-rose-500/10 text-rose-500 border border-rose-500/20 rounded-full flex items-center justify-center mx-auto">
            <Skull className="w-6 h-6 animate-bounce" />
          </div>
          
          <div className="space-y-0.5">
            <span className="text-rose-500 font-mono text-[10px] tracking-widest uppercase font-bold animate-pulse">
              ⚠ PILOT DOWN - JET CRASHED ⚠
            </span>
            <h2 className="text-3xl font-black text-white tracking-tight">
              DEBRIEFING COMPLETE
            </h2>
            <p className="text-xs text-slate-400 font-mono">
              TACTICAL EXERCISE RECORDED BY HQ SYSTEMS
            </p>
          </div>
        </div>

        {/* Record Alerts (New Best Score!) */}
        {isNewRecord && (
          <div className="my-4 p-3 bg-yellow-400/10 border border-yellow-500/20 text-yellow-400 rounded-xl flex items-center justify-center gap-2 text-xs font-mono font-bold">
            <Award className="w-4 h-4 fill-yellow-400/20 text-yellow-400" />
            NEW HIGH SCORE RECORD LOGGED IN COCKPIT!
          </div>
        )}

        {/* Score and level details board */}
        <div className="my-5 bg-slate-900/60 border border-slate-800 text-center font-mono rounded-xl overflow-hidden divide-y divide-slate-800">
          <div className="p-4 bg-slate-900/40">
            <div className="text-xs text-slate-400 mb-0.5 uppercase tracking-widest">FINAL COMBAT SCORE</div>
            <div className="text-3xl font-extrabold text-white tracking-tight">{score.toLocaleString()}</div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-800 text-xs">
            <div className="p-3">
              <span className="block text-slate-400">LEVEL REACHED</span>
              <span className="text-lg font-bold text-cyan-400">{levelReached}</span>
            </div>
            <div className="p-3">
              <span className="block text-slate-400">TOTAL KILLS</span>
              <span className="text-lg font-bold text-rose-400">{kills}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-800 text-xs">
            <div className="p-2.5">
              <span className="block text-slate-500 text-[10px]">DIFFICULTY</span>
              <span className="font-bold text-slate-300 uppercase tracking-widest">{difficulty}</span>
            </div>
            <div className="p-2.5">
              <span className="block text-slate-500 text-[10px]">PREVIOUS RECORD</span>
              <span className="font-bold text-slate-300">{bestScore.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Controls action buttons */}
        <div className="space-y-3">
          <button
            id="gameover-restart-btn"
            onClick={onRestart}
            className="w-full py-3.5 rounded-xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 text-slate-950 hover:from-orange-400 hover:to-amber-400 transition transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer text-sm"
          >
            <RotateCcw className="w-4 h-4 text-slate-950" /> FLIGHT RE-DEPLOYMENT
          </button>

          <button
            id="gameover-exit-btn"
            onClick={onExit}
            className="w-full py-2.5 rounded-xl font-medium bg-slate-900 text-slate-300 hover:text-white border border-slate-800 hover:bg-slate-800 transition flex items-center justify-center gap-2 cursor-pointer text-xs"
          >
            <Home className="w-4 h-4" /> RE-ENTER COMBAT BASE MENU
          </button>
        </div>

        <p className="mt-5 text-[10px] text-slate-500 font-mono text-center tracking-normal leading-normal">
          Pilot tip: In Veteran or Ace mode, heavy turret missile launcher bullets can be absorbed by barriers! Gather shields frequently.
        </p>

      </div>
    </div>
  );
};
